import React, { useEffect, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../assets/styles/SupervisorDashboard.css";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import FullPageLoader from "../../components/ui/fullPageLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const SupervisorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/users/supervisor-dashboard");
        setData(res.data);
      } catch (err) {
        console.error("Error fetching data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const goToStudentView = () => {
    navigate("/student-dashboard");
  };

  if (loading) {
    return <FullPageLoader loadingName="Loading user Dashboard..." />;
  }

  if (!data)
    return (
      <div className="sup-dashboard-container text-center text-danger py-5">
        Failed to load data
      </div>
    );

  // --- Chart Data (For Desktop) ---
  const barData = {
    labels: ["Academic", "Sports", "Entertainment", "Competitions"],
    datasets: [
      {
        label: "Activities",
        data: [
          data.categoryStats?.academic || 0,
          data.categoryStats?.sports || 0,
          data.categoryStats?.entertainment || 0,
          data.categoryStats?.competition || 0,
        ],
        backgroundColor: ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b"],
        borderRadius: 6,
        barThickness: 40,
      },
    ],
  };

  const doughnutData = {
    labels: ["Finished", "Active", "Cancelled", "Pending"],
    datasets: [
      {
        data: [data.FINISHED, data.ACTIVE, data.CANCELLED, data.PENDING],
        backgroundColor: ["#10b981", "#3b82f6", "#ef4444", "#f59e0b"],
        borderWidth: 2,
        borderColor: "#ffffff",
        hoverOffset: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { borderDash: [5, 5] },
        ticks: { stepSize: 1 },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      legend: {
        position: "right",
        labels: { usePointStyle: true, boxWidth: 8 },
      },
    },
  };

  // --- Mobile Data Helpers ---
  // بنجهز الداتا عشان نعرضها في ليستة بدل الشارت
  const categoriesList = [
    {
      name: "Academic",
      count: data.categoryStats?.academic || 0,
      color: "#6366f1",
      icon: "bi-book",
    },
    {
      name: "Sports",
      count: data.categoryStats?.sports || 0,
      color: "#8b5cf6",
      icon: "bi-trophy",
    },
    {
      name: "Entertainment",
      count: data.categoryStats?.entertainment || 0,
      color: "#ec4899",
      icon: "bi-controller",
    },
    {
      name: "Competition",
      count: data.categoryStats?.competition || 0,
      color: "#f59e0b",
      icon: "bi-lightning",
    },
  ];

  const statusList = [
    { name: "Finished", count: data.FINISHED, color: "#10b981" },
    { name: "Active", count: data.ACTIVE, color: "#3b82f6" },
    { name: "Pending", count: data.PENDING, color: "#f59e0b" },
    { name: "Cancelled", count: data.CANCELLED, color: "#ef4444" },
  ];

  return (
    <div style={{ marginTop: "75px" }} className="sup-dashboard-container">
      {/* Header & Stats Grid (زي ما هم) */}
      <header className="sup-header">
        <div className="sup-header-glass"></div>
        <div className="sup-header-content">
          <div>
            <h2>Supervisor Control</h2>
            <p>Overview & Analytics</p>
          </div>
          <button onClick={goToStudentView} className="btn-switch-role">
            <i className="bi bi-arrow-repeat"></i> <span>Student View</span>
          </button>
        </div>
        <div className="sup-reach-badge">
          <small>Total Reach</small>
          <strong>{data.totalStudentsReach}</strong>
        </div>
      </header>

      <div className="sup-stats-grid">
        <StatCard
          label="Total Activities"
          value={data.totalManagedActivities}
          icon="bi-briefcase-fill"
          color="purple"
        />
        <StatCard
          label="Engagement"
          value={`${data.engagementRate}%`}
          icon="bi-people-fill"
          color="blue"
        />
        <StatCard
          label="Active Now"
          value={data.ACTIVE}
          icon="bi-broadcast"
          color="green"
        />
        <StatCard
          label="Pending"
          value={data.PENDING}
          icon="bi-hourglass-split"
          color="orange"
        />
      </div>

      {/* =======================================================
          Desktop View: Charts (بيظهر بس في الشاشات الكبيرة)
         ======================================================= */}
      <div className="sup-charts-row desktop-only">
        <div className="sup-card chart-card">
          <div className="sup-card-header">
            <h5 className="sup-card-title">Activity Distribution</h5>
          </div>
          <div style={{ height: "300px" }}>
            <Bar data={barData} options={chartOptions} />
          </div>
        </div>
        <div className="sup-card chart-card">
          <div className="sup-card-header">
            <h5 className="sup-card-title">Status Overview</h5>
          </div>
          <div style={{ height: "300px" }}>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
        </div>
      </div>

      {/* =======================================================
          Mobile View: Summary Lists (بديل الشارتس للموبايل)
         ======================================================= */}
      <div className="mobile-stats-view mobile-only">
        {/* بديل الـ Bar Chart: Categories List */}
        <div className="sup-card mb-3">
          <div className="sup-card-header mb-3">
            <h5 className="sup-card-title">Activity Categories</h5>
          </div>
          <div className="mobile-list-container">
            {categoriesList.map((cat, idx) => (
              <div key={idx} className="mobile-stat-row">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="mobile-icon-box"
                    style={{ background: `${cat.color}20`, color: cat.color }}
                  >
                    <i className={`bi ${cat.icon}`}></i>
                  </div>
                  <span className="fw-bold text-dark">{cat.name}</span>
                </div>
                <span
                  className="mobile-stat-badge"
                  style={{ background: cat.color }}
                >
                  {cat.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* بديل الـ Doughnut Chart: Status Grid */}
        <div className="sup-card mb-3">
          <div className="sup-card-header mb-3">
            <h5 className="sup-card-title">Status Breakdown</h5>
          </div>
          <div className="mobile-status-grid">
            {statusList.map((stat, idx) => (
              <div
                key={idx}
                className="mobile-status-item"
                style={{ borderLeft: `4px solid ${stat.color}` }}
              >
                <span className="text-muted small text-uppercase">
                  {stat.name}
                </span>
                <span className="fs-4 fw-bold text-dark">{stat.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks List (زي ما هي) */}
      <div className="sup-card">
        <div className="sup-card-header">
          <h5 className="sup-card-title">
            <i className="bi bi-calendar-event text-warning me-2"></i> Upcoming
            Duties
          </h5>
        </div>
        <div className="sup-tasks-list">
          {data.calendar && data.calendar.length > 0 ? (
            data.calendar.map((item, index) => (
              <div key={index} className={`sup-task-item ${item.status}`}>
                <div className="sup-task-main">
                  <div className="sup-task-date">
                    <span className="day">{item.startDate.split(" ")[0]}</span>
                    <span className="time">
                      {item.startDate.split(" ")[1] || "Today"}
                    </span>
                  </div>
                  <div className="sup-task-info">
                    <h5>{item.event}</h5>
                    <div className="sup-task-meta">
                      <i className="bi bi-people"></i> {item.participantsCount}{" "}
                      Students
                    </div>
                  </div>
                </div>
                <span
                  className={`sup-badge ${item.status === "ACTIVE" ? "badge-active" : "badge-pending"}`}
                >
                  {item.status}
                </span>
              </div>
            ))
          ) : (
            <div className="empty-state">No upcoming activities found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, color }) => (
  <div className="sup-card stat-card">
    <div className="stat-icon-wrapper" data-color={color}>
      <i className={`bi ${icon}`}></i>
    </div>
    <div className="stat-content">
      <div className="sup-stat-value">{value}</div>
      <div className="sup-stat-label">{label}</div>
    </div>
  </div>
);

export default SupervisorDashboard;

// import React, { useEffect, useState } from "react";
// import { Bar, Doughnut } from "react-chartjs-2";
// import {
//   Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend,
// } from "chart.js";
// import "bootstrap-icons/font/bootstrap-icons.css";
// import "../../assets/styles/SupervisorDashboard.css";
// import api from "../../services/api";
// import { useNavigate } from "react-router-dom";

// ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// const SupervisorDashboard = () => {
//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const res = await api.get("/users/supervisor-dashboard");
//         setData(res.data);
//       } catch (err) {
//         console.error("Error fetching data", err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   const goToStudentView = () => {
//     navigate("/student-dashboard");
//   };

//   if (loading) return (
//     <div className="sup-dashboard-container d-flex justify-content-center align-items-center" style={{minHeight: '80vh'}}>
//       <div className="spinner-border text-primary" role="status"></div>
//     </div>
//   );

//   if (!data) return <div className="sup-dashboard-container text-center text-danger py-5">Failed to load data</div>;

//   // --- Chart Configurations ---
//   const barData = {
//     labels: ["Academic", "Sports", "Ent.", "Comp."],
//     datasets: [{
//       label: "Activities",
//       data: [
//         data.categoryStats?.academic || 0,
//         data.categoryStats?.sports || 0,
//         data.categoryStats?.entertainment || 0,
//         data.categoryStats?.competition || 0,
//       ],
//       backgroundColor: ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b"],
//       borderRadius: 6,
//       barThickness: 'flex',
//       maxBarThickness: 35, // قللته سنة عشان الموبايل
//     }],
//   };

//   const doughnutData = {
//     labels: ["Finished", "Active", "Cancelled", "Pending"],
//     datasets: [{
//       data: [data.FINISHED, data.ACTIVE, data.CANCELLED, data.PENDING],
//       backgroundColor: ["#10b981", "#3b82f6", "#ef4444", "#f59e0b"],
//       borderWidth: 2,
//       borderColor: '#ffffff',
//       hoverOffset: 4
//     }],
//   };

//   // ⚠️ مهم جداً: Chart Options
//   // لازم maintainAspectRatio تكون false عشان تسمع كلام الـ CSS wrapper
//   const chartOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { display: false },
//       tooltip: {
//         backgroundColor: 'rgba(30, 41, 59, 0.9)',
//         padding: 10,
//         cornerRadius: 6,
//         displayColors: false,
//       }
//     },
//     scales: {
//       x: { grid: { display: false }, ticks: { font: { size: 10 } } },
//       y: { beginAtZero: true, grid: { borderDash: [5, 5], color: '#f1f5f9' }, ticks: { stepSize: 1 } }
//     }
//   };

//   const doughnutOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     cutout: '75%',
//     plugins: {
//       legend: {
//         position: 'bottom',
//         labels: { usePointStyle: true, padding: 15, font: { size: 11 }, boxWidth: 8 }
//       }
//     }
//   };

//   return (
//     <div className="sup-dashboard-container">

//       {/* --- 1. Header Section --- */}
//       <header className="sup-header">
//         <div className="sup-header-glass"></div>
//         <div className="sup-header-content">
//           <div>
//             <h2>Supervisor Control</h2>
//             <p>Overview & Analytics</p>
//           </div>

//           <button onClick={goToStudentView} className="btn-switch-role">
//             <i className="bi bi-arrow-repeat"></i>
//             <span>Student View</span>
//           </button>
//         </div>

//         <div className="sup-reach-badge">
//           <small>Total Reach</small>
//           <strong>{data.totalStudentsReach}</strong>
//         </div>
//       </header>

//       {/* --- 2. Stats Grid --- */}
//       <div className="sup-stats-grid">
//         <StatCard label="Total Activities" value={data.totalManagedActivities} icon="bi-briefcase-fill" color="purple" />
//         <StatCard label="Engagement" value={`${data.engagementRate}%`} icon="bi-people-fill" color="blue" />
//         <StatCard label="Active Now" value={data.ACTIVE} icon="bi-broadcast" color="green" />
//         <StatCard label="Pending" value={data.PENDING} icon="bi-hourglass-split" color="orange" />
//       </div>

//       {/* --- 3. Charts Section (Fixed) --- */}
//       <div className="sup-charts-row">
//         {/* Bar Chart */}
//         <div className="sup-card chart-card">
//           <div className="sup-card-header">
//             <h5 className="sup-card-title">Activity Distribution</h5>
//           </div>
//           {/* 🔥 ده التعديل المهم: ضفنا كلاس chart-wrapper */}
//           <div className="chart-wrapper">
//             <Bar data={barData} options={chartOptions} />
//           </div>
//         </div>

//         {/* Doughnut Chart */}
//         <div className="sup-card chart-card">
//           <div className="sup-card-header">
//             <h5 className="sup-card-title">Status</h5>
//           </div>
//           {/* 🔥 ده التعديل المهم: ضفنا كلاس doughnut-wrapper */}
//           <div className="chart-wrapper doughnut-wrapper">
//              <Doughnut data={doughnutData} options={doughnutOptions} />
//           </div>
//         </div>
//       </div>

//       {/* --- 4. Tasks List --- */}
//       <div className="sup-card">
//         <div className="sup-card-header">
//           <h5 className="sup-card-title">
//             <i className="bi bi-calendar-event text-warning me-2"></i> Upcoming Duties
//           </h5>
//         </div>

//         <div className="sup-tasks-list">
//           {data.calendar && data.calendar.length > 0 ? (
//             data.calendar.map((item, index) => (
//               <div key={index} className={`sup-task-item ${item.status}`}>
//                 <div className="sup-task-main">
//                   <div className="sup-task-date">
//                     <span className="day">{item.startDate.split(' ')[0]}</span>
//                     <span className="time">{item.startDate.split(' ')[1] || 'Today'}</span>
//                   </div>
//                   <div className="sup-task-info">
//                     <h5>{item.event}</h5>
//                     <div className="sup-task-meta">
//                       <i className="bi bi-people"></i> {item.participantsCount} Students
//                     </div>
//                   </div>
//                 </div>

//                 <span className={`sup-badge ${item.status === 'ACTIVE' ? 'badge-active' : 'badge-pending'}`}>
//                   {item.status}
//                 </span>
//               </div>
//             ))
//           ) : (
//             <div className="empty-state">No upcoming activities found.</div>
//           )}
//         </div>
//       </div>

//     </div>
//   );
// };

// // Sub-component
// const StatCard = ({ label, value, icon, color }) => (
//   <div className="sup-card stat-card">
//     <div className="stat-icon-wrapper" data-color={color}>
//       <i className={`bi ${icon}`}></i>
//     </div>
//     <div className="stat-content">
//       <div className="sup-stat-value">{value}</div>
//       <div className="sup-stat-label">{label}</div>
//     </div>
//   </div>
// );

// export default SupervisorDashboard;
